/**
 * aecdxc-collection-view.js
 * Horizon 4.1.3+ compatible. Verified against Shopify/horizon @ theme_version 4.1.3.
 *
 * Defines two custom elements:
 *   <aecdxc-collection-toolbar>  view toggle + Add All / Clear / Add Selected
 *   <aecdxc-collection-list>     the table, self-refreshing on filter change
 *
 * Design notes (why it is built this way):
 *
 *  1. Custom elements, not a deferred IIFE.
 *     facets.js calls sectionRenderer.renderSection(sectionId) on every filter,
 *     sort and grid-density change, which destroys and replaces the whole
 *     `main` section. connectedCallback re-runs on the replacement node.
 *
 *  2. View state lives as a class on <html>, not as inline styles.
 *     An ancestor class survives section re-renders for free. Inline styles set
 *     on section nodes do not.
 *
 *  3. Cart updates use CartLinesUpdateEvent from @shopify/events.
 *     Horizon 4.x cart-icon.js listens for 'shopify:cart:lines-update' and
 *     reads event.promise. A plain CustomEvent named 'cart:update' is ignored.
 *
 *  4. Variant IDs are read from the list table, never from grid card markup.
 *     The table is always in the DOM (hidden in grid mode), so this works in
 *     both views and does not depend on Horizon's product-card internals.
 *     Requires the two sections to use the same products-per-page value.
 *
 *  5. Section refresh uses the plain Section Rendering API (?section_id=),
 *     not Horizon's internal @theme/section-renderer module, so a theme
 *     refactor of that module cannot break it.
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const VIEW_KEY = 'aecdxc-collection-view';
const LIST_CLASS = 'aecdxc-view-list';
const CART_SOURCE = 'aecdxc-collection-view';

/* ============================================================
   View state
   ============================================================ */

function readView() {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch (e) {
    return 'grid';
  }
}

function applyView(view) {
  document.documentElement.classList.toggle(LIST_CLASS, view === 'list');

  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch (e) {
    /* private browsing, ignore */
  }

  for (const btn of document.querySelectorAll('[data-aecdxc-view]')) {
    const active = btn.dataset.aecdxcView === view;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  document.dispatchEvent(new CustomEvent('aecdxc:view-change', { detail: { view } }));
}

/* ============================================================
   Cart helpers
   ============================================================ */

async function fetchCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Could not read cart (${res.status})`);
  return res.json();
}

function cartAddUrl() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
}

/**
 * Adds variants and tells Horizon's cart icon and drawer about it.
 * @param {HTMLElement} dispatcher element the event bubbles from
 * @param {number[]} variantIds
 */
async function addVariantsToCart(dispatcher, variantIds) {
  const deferred = CartLinesUpdateEvent.createPromise();

  dispatcher.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'collection',
      lines: variantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: variantIds.map((id) => ({ id, quantity: 1 })) }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || `Cart add failed (${res.status})`);
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { itemCount: cart.item_count, items: cart.items, source: CART_SOURCE },
    });
    return cart;
  } catch (error) {
    // Resolve with the real cart rather than rejecting, so the cart bubble
    // settles on an accurate count instead of logging an unhandled rejection.
    const cart = await fetchCart().catch(() => null);
    deferred.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail: { didError: true, itemCount: cart ? cart.item_count : 0, source: CART_SOURCE },
    });
    throw error;
  }
}

/* ============================================================
   Shared variant discovery
   ============================================================ */

function listElement() {
  return document.querySelector('aecdxc-collection-list');
}

/** Every variant on the current page, in table order. */
function pageVariantIds(cap) {
  const list = listElement();
  const ids = new Set();

  if (list) {
    for (const cb of list.querySelectorAll('.aecdxc-row-check[data-variant-id]')) {
      if (ids.size >= cap) break;
      const id = parseInt(cb.dataset.variantId, 10);
      if (id) ids.add(id);
    }
  }

  // Fallback for collections rendered without the list section.
  if (ids.size === 0) {
    const grid = document.querySelector('results-list');
    if (grid) {
      const selector = 'form[action*="/cart/add"] input[name="id"]:not([disabled])';
      for (const input of grid.querySelectorAll(selector)) {
        if (ids.size >= cap) break;
        const id = parseInt(input.value, 10);
        if (id) ids.add(id);
      }
    }
  }

  return Array.from(ids);
}

function selectedVariantIds() {
  const list = listElement();
  if (!list) return [];

  const ids = new Set();
  for (const cb of list.querySelectorAll('.aecdxc-row-check:checked')) {
    const id = parseInt(cb.dataset.variantId, 10);
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

/* ============================================================
   <aecdxc-collection-toolbar>
   ============================================================ */

class AecdxcCollectionToolbar extends HTMLElement {
  connectedCallback() {
    this.cap = parseInt(this.dataset.cap, 10) || 100;
    this.statusEl = this.querySelector('[data-aecdxc-status]');
    this.selectedBtn = this.querySelector('[data-aecdxc-action="add-selected"]');
    this.selectedCountEl = this.querySelector('[data-aecdxc-selected-count]');

    this.addEventListener('click', this.onClick);
    document.addEventListener('change', this.onDocumentChange);
    document.addEventListener('aecdxc:list-refreshed', this.syncSelection);

    applyView(readView());
    this.syncSelection();
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onDocumentChange);
    document.removeEventListener('aecdxc:list-refreshed', this.syncSelection);
  }

  /* ---- events ---- */

  onClick = (event) => {
    const viewBtn = event.target.closest('[data-aecdxc-view]');
    if (viewBtn) {
      applyView(viewBtn.dataset.aecdxcView);
      return;
    }

    const actionBtn = event.target.closest('[data-aecdxc-action]');
    if (!actionBtn) return;

    switch (actionBtn.dataset.aecdxcAction) {
      case 'add-all':
        this.addAll();
        break;
      case 'add-selected':
        this.addSelected();
        break;
      case 'clear':
        this.clearCart();
        break;
    }
  };

  onDocumentChange = (event) => {
    if (event.target.closest('.aecdxc-row-check, #aecdxc-select-all')) this.syncSelection();
  };

  syncSelection = () => {
    if (!this.selectedBtn) return;
    const count = selectedVariantIds().length;
    this.selectedBtn.disabled = count === 0;
    if (this.selectedCountEl) this.selectedCountEl.textContent = count ? `(${count})` : '';
  };

  /* ---- actions ---- */

  async addAll() {
    await this.run('add-all', 'Add all to cart', async () => {
      const ids = pageVariantIds(this.cap);
      if (ids.length === 0) return 'No purchasable products on this page.';

      const cart = await fetchCart();
      const inCart = new Set(cart.items.map((i) => i.variant_id));
      const toAdd = ids.filter((id) => !inCart.has(id));

      if (toAdd.length === 0) return 'Everything on this page is already in your cart.';

      await addVariantsToCart(this, toAdd);

      let msg = `${toAdd.length} ${toAdd.length === 1 ? 'family' : 'families'} added to cart.`;
      if (ids.length >= this.cap) msg += ` Capped at the first ${this.cap} on this page.`;
      return msg;
    });
  }

  async addSelected() {
    await this.run('add-selected', 'Add selected', async () => {
      const ids = selectedVariantIds();
      if (ids.length === 0) return 'Nothing selected.';

      const cart = await fetchCart();
      const inCart = new Set(cart.items.map((i) => i.variant_id));
      const toAdd = ids.filter((id) => !inCart.has(id));

      if (toAdd.length === 0) return 'Your selection is already in your cart.';

      await addVariantsToCart(this, toAdd);

      const list = listElement();
      if (list) list.clearSelection();
      this.syncSelection();

      return `${toAdd.length} ${toAdd.length === 1 ? 'family' : 'families'} added to cart.`;
    });
  }

  async clearCart() {
    if (!window.confirm('Remove all items from your cart?')) return;

    await this.run('clear', 'Clear cart', async () => {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({ action: 'remove', context: 'cart', promise: deferred.promise })
      );

      try {
        const res = await fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        });
        if (!res.ok) throw new Error(`Clear failed (${res.status})`);

        const cart = await fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { itemCount: cart.item_count, source: CART_SOURCE },
        });
      } catch (error) {
        deferred.resolve({
          cart: null,
          detail: { didError: true, itemCount: 0, source: CART_SOURCE },
        });
        throw error;
      }

      return 'Cart cleared.';
    });
  }

  /**
   * Runs an action with busy state and status messaging.
   * @param {string} action data-aecdxc-action value
   * @param {string} label idle button label
   * @param {() => Promise<string>} work resolves to the status message
   */
  async run(action, label, work) {
    const btn = this.querySelector(`[data-aecdxc-action="${action}"]`);
    const buttons = Array.from(this.querySelectorAll('[data-aecdxc-action]'));

    for (const b of buttons) b.disabled = true;
    if (btn) btn.dataset.busy = 'true';
    this.setStatus('');

    try {
      this.setStatus(await work(), 'success');
    } catch (error) {
      this.setStatus('Something went wrong. Please try again.', 'error');
      console.error('[aecdxc-collection-view]', error);
    } finally {
      for (const b of buttons) b.disabled = false;
      if (btn) delete btn.dataset.busy;
      this.syncSelection();
    }
  }

  setStatus(msg, type) {
    if (!this.statusEl) return;
    this.statusEl.textContent = msg || '';
    this.statusEl.className =
      'aecdxc-toolbar__status' + (msg && type ? ` aecdxc-toolbar__status--${type}` : '');
  }
}

/* ============================================================
   <aecdxc-collection-list>
   ============================================================ */

class AecdxcCollectionList extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;

    // Delegated on `this`, so listeners survive innerHTML swaps on refresh.
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    document.addEventListener(StandardEvents.collectionUpdate, this.onCollectionUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.collectionUpdate, this.onCollectionUpdate);
    if (this.#controller) this.#controller.abort();
  }

  /* ---- filter awareness ---- */

  // facets.js pushes the new URL to history before dispatching this event,
  // so window.location already carries the active filters.
  onCollectionUpdate = () => this.refresh();

  async refresh() {
    if (!this.sectionId) return;

    if (this.#controller) this.#controller.abort();
    this.#controller = new AbortController();

    this.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);

      const res = await fetch(url, {
        headers: { Accept: 'text/html' },
        signal: this.#controller.signal,
      });
      if (!res.ok) throw new Error(`Section render failed (${res.status})`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('aecdxc-collection-list');
      if (next) this.innerHTML = next.innerHTML;

      document.dispatchEvent(new CustomEvent('aecdxc:list-refreshed'));
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[aecdxc-collection-list]', error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  /* ---- selection ---- */

  onChange = (event) => {
    const selectAll = event.target.closest('#aecdxc-select-all');
    if (selectAll) {
      for (const cb of this.querySelectorAll('.aecdxc-row-check')) cb.checked = selectAll.checked;
    }
    this.syncRows();
  };

  syncRows() {
    const boxes = Array.from(this.querySelectorAll('.aecdxc-row-check'));
    let checked = 0;

    for (const cb of boxes) {
      if (cb.checked) checked += 1;
      const row = cb.closest('.aecdxc-list__row');
      if (row) row.classList.toggle('is-selected', cb.checked);
    }

    const selectAll = this.querySelector('#aecdxc-select-all');
    if (selectAll) {
      selectAll.checked = checked > 0 && checked === boxes.length;
      selectAll.indeterminate = checked > 0 && checked < boxes.length;
    }
  }

  clearSelection() {
    for (const cb of this.querySelectorAll('.aecdxc-row-check')) cb.checked = false;
    this.syncRows();
  }

  /* ---- single row add ---- */

  onClick = async (event) => {
    const btn = event.target.closest('[data-aecdxc-add-row]');
    if (!btn) return;

    const id = parseInt(btn.dataset.variantId, 10);
    if (!id) return;

    const original = btn.innerHTML;
    btn.disabled = true;
    btn.dataset.busy = 'true';

    try {
      await addVariantsToCart(this, [id]);
      btn.dataset.state = 'added';
      btn.textContent = 'Added';
    } catch (error) {
      btn.dataset.state = 'error';
      btn.textContent = 'Retry';
      console.error('[aecdxc-collection-list]', error);
    } finally {
      delete btn.dataset.busy;
      setTimeout(() => {
        if (!btn.isConnected) return;
        delete btn.dataset.state;
        btn.innerHTML = original;
        btn.disabled = false;
      }, 2000);
    }
  };
}

/* ============================================================
   Register
   ============================================================ */

if (!customElements.get('aecdxc-collection-toolbar')) {
  customElements.define('aecdxc-collection-toolbar', AecdxcCollectionToolbar);
}

if (!customElements.get('aecdxc-collection-list')) {
  customElements.define('aecdxc-collection-list', AecdxcCollectionList);
}
